import { FC, useState } from "react";
import { Control, useWatch } from "react-hook-form";
import {
  FormController,
  MuiBox,
  MuiGrid,
  MuiLink,
  MuiSelect,
  MuiTextField,
} from "../../../../../../../../components";
import { ManageLocale } from "../../../../../../../../components/manageLocale/ManageLocale";
import { MAX_LENGTH_75 } from "../../../../../../../../helpers/constants";
import { UnitTypesKey } from "../../../../../../../../helpers/constants/siteCoreKeys";
import { KeyValue } from "../../../../../../../../helpers/types/common";
import { useNavigationSiteCore } from "../../../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../../../hooks/useSiteCore";
import { activitiesAndDeliverablesSiteCoreKeys } from "../../../../../../siteCore/SiteCoreKeys";
import {
  CreateDeliverablesFormKeys,
  CreateDeliverablesFormProps,
} from "../../../../modelsAndSchemas/models";
import { NewDeliverableWrapper } from "./NewDeliverable.style";

const gridSize = { md: 4, sm: 6, xs: 12 };

export interface NewDeliverableProps {
  pageId: string;
  selectedUnit: string;
  control: Control<CreateDeliverablesFormProps>;
  handleUnitChange: (unit: string) => void;
  manageLocaleTitle: string;
  manageLocaleInfoMessage: string;
  handleSaveManageLocal: (localeData: KeyValue) => void;
  userSelectedLanguage: string;
}

const NewDeliverable: FC<NewDeliverableProps> = ({
  pageId,
  selectedUnit,
  control,
  handleUnitChange,
  manageLocaleTitle,
  manageLocaleInfoMessage,
  userSelectedLanguage,
  handleSaveManageLocal,
}) => {
  const {
    commonLabels,
    fetchLabel,
    fetchDropdownValues,
    commonValidationMessages,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();

  const [showManageLocale, setShowManageLocale] = useState(false);

  const handleShowManageLocale = () => {
    setShowManageLocale(true);
  };

  const handleCloseShowManageLocale = () => {
    setShowManageLocale(false);
  };

  const deliverablesNames = useWatch({
    name: CreateDeliverablesFormKeys.name,
    control,
  });

  const renderMeasurement = (
    measurementOtherMinuteName: string,
    measurementOther: string,
  ) => {
    return (
      <>
        {selectedUnit === UnitTypesKey.Minutes && (
          <FormController
            control={control}
            name={
              measurementOtherMinuteName as keyof CreateDeliverablesFormProps
            }
          >
            <MuiSelect
              label={fetchLabel(
                activitiesAndDeliverablesSiteCoreKeys.createDeliverableKey,
                activitiesAndDeliverablesSiteCoreKeys.createDeliverableMinutes,
              )}
              visibleDefaultSelectOption
              options={fetchDropdownValues(
                activitiesAndDeliverablesSiteCoreKeys.createDeliverableKey,
                activitiesAndDeliverablesSiteCoreKeys.createDeliverableMinuteList,
              )}
              isMandatory
              id={`${pageId}-${measurementOtherMinuteName}`}
              placeholder={commonLabels.selectPlaceHolder}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        )}

        {(selectedUnit === UnitTypesKey.Count ||
          selectedUnit === UnitTypesKey.Days) && (
          <FormController
            control={control}
            name={measurementOther as keyof CreateDeliverablesFormProps}
          >
            <MuiTextField
              label={fetchLabel(
                activitiesAndDeliverablesSiteCoreKeys.createDeliverableKey,
                selectedUnit === UnitTypesKey.Count
                  ? activitiesAndDeliverablesSiteCoreKeys.createDeliverableQuantity
                  : activitiesAndDeliverablesSiteCoreKeys.createDeliverableNumberOfDays,
              )}
              id={`${pageId}-${measurementOther}`}
              type="number"
              placeholder={commonLabels.inputPlaceHolder}
              isMandatory
              inputNumberWithoutDecimal
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        )}
      </>
    );
  };

  return (
    <>
      <NewDeliverableWrapper>
        <MuiGrid container className="form-container">
          <MuiGrid size={gridSize} className="">
            <FormController
              control={control}
              name={CreateDeliverablesFormKeys.deliverableType}
            >
              <MuiSelect
                label={fetchLabel(
                  activitiesAndDeliverablesSiteCoreKeys.createDeliverableKey,
                  activitiesAndDeliverablesSiteCoreKeys.deliverableType,
                )}
                options={fetchDropdownValues(
                  activitiesAndDeliverablesSiteCoreKeys.createDeliverableKey,
                  activitiesAndDeliverablesSiteCoreKeys.deliverableTypeList,
                )}
                isMandatory
                value=""
                visibleDefaultSelectOption
                id={`${pageId}-${CreateDeliverablesFormKeys.deliverableType}`}
                placeholder={commonLabels.selectPlaceHolder}
                requiredARIALabel={commonARIALabel.required}
                data-testid={`${pageId}-deliverable-type`}
              />
            </FormController>
          </MuiGrid>

          <MuiGrid size={gridSize}>
            <MuiBox className="deliverable-name">
              <FormController
                control={control}
                name={`${CreateDeliverablesFormKeys.name}.${userSelectedLanguage}`}
              >
                <MuiTextField
                  label={fetchLabel(
                    activitiesAndDeliverablesSiteCoreKeys.createDeliverableKey,
                    activitiesAndDeliverablesSiteCoreKeys.deliverableName,
                  )}
                  placeholder={commonLabels.inputPlaceHolder}
                  id={`${pageId}-${CreateDeliverablesFormKeys.name}`}
                  isMandatory
                  maxLength={MAX_LENGTH_75}
                  requiredARIALabel={commonARIALabel.required}
                  data-testid={`${pageId}-deliverable-name`}
                  tooltipMessage={
                    commonValidationMessages.fieldSpecialCharactersTooltipMessage
                  }
                  tooltipHeaderTitle={
                    commonValidationMessages.allowedSpecialCharactersTitle
                  }
                />
              </FormController>
              <MuiLink
                className="manage-locale-link"
                id={`${pageId}-locale`}
                data-testid={`${pageId}-locale`}
                onClick={handleShowManageLocale}
              >
                {commonLabels.locale}
              </MuiLink>
            </MuiBox>
          </MuiGrid>
          <MuiGrid size={gridSize}>
            <FormController
              control={control}
              name={CreateDeliverablesFormKeys.units}
            >
              <MuiSelect
                label={fetchLabel(
                  activitiesAndDeliverablesSiteCoreKeys.createDeliverableKey,
                  activitiesAndDeliverablesSiteCoreKeys.createDeliverableUnits,
                )}
                options={fetchDropdownValues(
                  activitiesAndDeliverablesSiteCoreKeys.createDeliverableKey,
                  activitiesAndDeliverablesSiteCoreKeys.createDeliverableUnitList,
                )}
                visibleDefaultSelectOption
                value=""
                id={`${pageId}-${CreateDeliverablesFormKeys.units}`}
                isMandatory
                placeholder={commonLabels.selectPlaceHolder}
                onChange={(e: any) =>
                  handleUnitChange(e.target.value as string)
                }
                requiredARIALabel={commonARIALabel.required}
                data-testid={`${pageId}-units`}
              />
            </FormController>
          </MuiGrid>
          <MuiGrid size={gridSize}>
            {renderMeasurement(
              CreateDeliverablesFormKeys.measurementMinute,
              CreateDeliverablesFormKeys.measurementOther,
            )}
          </MuiGrid>
        </MuiGrid>
      </NewDeliverableWrapper>
      {showManageLocale && (
        <ManageLocale
          show={showManageLocale}
          handleCloseModal={handleCloseShowManageLocale}
          localeLanguages={deliverablesNames}
          onSaveManageLocalCallback={handleSaveManageLocal}
          title={manageLocaleTitle}
          maxLength={MAX_LENGTH_75}
          bannerInfoMessage={manageLocaleInfoMessage}
        />
      )}
    </>
  );
};

export default NewDeliverable;
